import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import * as XLSX from 'xlsx';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  Divider,
} from '@mui/material';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';

const STATUS_OPTIONS = [
  'Hakediş Oluştu',
  'Fatura Yüklendi',
  'Fatura Ödendi',
  'Devreden Hakediş',
  'Gecikmiş Fatura',
  'Fatura Reddedildi',
  'Kontrol Edildi',
];

const STATUS_STYLES = {
  'Hakediş Oluştu': { color: '#1597D3', backgroundColor: '#E7F7FF' },
  'Fatura Yüklendi': { color: '#47A447', backgroundColor: '#EAF8E8' },
  'Kontrol Edildi': { color: '#7A5AF8', backgroundColor: '#F4F3FF' },
  'Fatura Ödendi': { color: '#FFFFFF', backgroundColor: '#48A23F' },
  'Fatura Reddedildi': { color: '#E14A3B', backgroundColor: '#FFE8E5' },
  'Gecikmiş Fatura': { color: '#DD6B20', backgroundColor: '#FFF0E1' },
  'Devreden Hakediş': { color: '#B545D4', backgroundColor: '#F8E5FF' },
};

const STATUS_CODE_MAP = {
  3: 'Hakediş Oluştu',
  4: 'Fatura Yüklendi',
  5: 'Fatura Ödendi',
  7: 'Devreden Hakediş',
  8: 'Gecikmiş Fatura',  
  9: 'Fatura Reddedildi',
  10: 'Kontrol Edildi',  //Esnaf App'te Hakediş Oluştu olarak gözükecek. 
};

const STATUS_VALUE_MAP = {
  'Hakediş Oluştu': 3,
  'Fatura Yüklendi': 4,
  'Fatura Ödendi': 5,
  'Devreden Hakediş': 7,
  'Gecikmiş Fatura': 8,
  'Fatura Reddedildi': 9,
  'Kontrol Edildi': 10,
};

const MIN_MONTH = dayjs('2026-06-01');
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const EXCEL_SUBE_KODU_KEY = 'SubeKodu';
const EXCEL_NOKTA_ID_KEY = 'NoktaId';

const formatCurrency = (value) =>
  `${Number(value || 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}₺`;

const normalizeDateValue = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim();
    const dottedDateMatch = normalizedValue.match(
      /^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );

    if (dottedDateMatch) {
      const [, day, month, year, hour = '00', minute = '00', second = '00'] =
        dottedDateMatch;
      const parsedFromDottedDate = dayjs(
        `${year}-${month}-${day}T${hour}:${minute}:${second}`
      );

      if (parsedFromDottedDate.isValid()) {
        return parsedFromDottedDate;
      }
    }
  }

  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const numericValue = Number(value);
    if (String(Math.trunc(numericValue)).length === 10) {
      return dayjs.unix(numericValue);
    }
    if (String(Math.trunc(numericValue)).length === 13) {
      return dayjs(numericValue);
    }
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const formatDate = (value) =>
  normalizeDateValue(value)?.locale('tr').format('DD.MM.YYYY') || '-';
const formatMonthLabel = (value) =>
  value ? dayjs(`${value}-01`).locale('tr').format('MMMM YYYY') : '-';
const formatReadStatus = (value) =>
  value !== null &&
  value !== undefined &&
  String(value).trim() !== '' &&
  String(value) !== '0'
    ? 'Evet'
    : 'Hayır';

const buildMonthRange = (startMonth, endMonth) => {
  const months = [];
  let cursor = startMonth.startOf('month');
  const finish = endMonth.startOf('month');

  while (cursor.isBefore(finish) || cursor.isSame(finish, 'month')) {
    months.push(cursor.format('YYYY-MM'));
    cursor = cursor.add(1, 'month');
  }

  return months;
};

const buildMonthOptions = () => {
  const options = [];
  let cursor = MIN_MONTH.startOf('month');
  const finish = dayjs().startOf('month');

  while (cursor.isBefore(finish) || cursor.isSame(finish, 'month')) {
    options.push({
      value: cursor.format('YYYY-MM'),
      label: cursor.locale('tr').format('MMMM YYYY'),
    });
    cursor = cursor.add(1, 'month');
  }

  return options;
};

const resolveInvoiceStatus = (item) => {
  if (item?.status && STATUS_CODE_MAP[Number(item.status)]) {
    return STATUS_CODE_MAP[Number(item.status)];
  }

  if (item?.invoiceFileURL) {
    return 'Fatura Yüklendi';
  }

  return 'Hakediş Oluştu';
};

const buildRowsFromResponse = (result, pointTitle) => {
  const list = Array.isArray(result) ? result : result ? [result] : [];

  return list.map((item, index) => {
    const month = item?.invoiceDate || item?.month || `row-${index + 1}`;
    const invoiceStatus = resolveInvoiceStatus(item);
    const amount = Number(
      item?.totalPrice || item?.total_price || item?.amount || 0
    );
    const cutOff = Array.isArray(item?.cutOff) ? item.cutOff : [];
    const additionalPayments = Array.isArray(item?.additionalPayments)
      ? item.additionalPayments
      : [];
    const cutOffAmount = cutOff.reduce(
      (sum, cutOffItem) => sum + Number(cutOffItem?.amount || 0),
      0
    );
    const additionalPaymentAmount = additionalPayments.reduce(
      (sum, additionalPaymentItem) =>
        sum + Number(additionalPaymentItem?.amount || 0),
      0
    );
    const netAmount = Math.max(
      amount - cutOffAmount + additionalPaymentAmount,
      0
    );

    return {
      id: item?.id || item?.invoice_id || `${month}-${index}`,
      invoiceID: item?.invoice_id || item?.invoiceID || item?.invoiceId || null,
      pointId: item?.poiID || item?.pointID || null,
      pointName:
        item?.title || pointTitle || item?.pointTitle || 'Nokta Bilgisi',
      month,
      createdDate: item?.createdDateTime || null,
      paymentDate: item?.paymentDate || null,
      uploadDate: item?.uploadDate || item?.createdDateTime || null,
      uploadStatus: item?.invoiceFileURL ? 'Yüklendi' : 'Yüklenmedi',
      isRead: formatReadStatus(item?.is_read),
      invoiceStatus,
      amount: netAmount,
      cutOff,
      additionalPayments,
      invoiceFileURL: item?.invoiceFileURL || null,
      rawItem: item,
    };
  });
};

const normalizeMatchKey = (value) =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase('tr');

const normalizeHeaderSearchValue = (value) =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase('tr');

const HEADER_FILTER_FIELDS = [
  { key: 'pointName', label: 'Nokta Adı' },
  { key: 'month', label: 'Hakediş Ayı' },
  { key: 'paymentDate', label: 'Ödeme Tarihi' },
  { key: 'uploadDate', label: 'Yüklenme Tarihi' },
  { key: 'uploadStatus', label: 'Yükleme Durumu' },
  { key: 'isRead', label: 'Görülme Durumu' },
  { key: 'invoiceStatus', label: 'Fatura Statüsü' },
  { key: 'amount', label: 'Tutar' },
];

function StatusChip({ label }) {
  const style = STATUS_STYLES[label] || STATUS_STYLES['Hakediş Oluştu'];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.25,
        minHeight: 22,
        borderRadius: '999px',
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        color: style.color,
        backgroundColor: style.backgroundColor,
      }}
    >
      {label}
    </Box>
  );
}

export default function HakedisManagementPage() {
  const selectMenuWidth = 240;
  const navigate = useNavigate();
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [branches, setBranches] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState('');
  const [startMonth, setStartMonth] = useState(MIN_MONTH);
  const [endMonth, setEndMonth] = useState(dayjs().startOf('month'));
  const [statusFilter, setStatusFilter] = useState('');
  const [pointFilterText, setPointFilterText] = useState('');
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('Fatura Ödendi');
  const [dataWarning, setDataWarning] = useState('');
  const [page, setPage] = useState(1);
  const [filterTick, setFilterTick] = useState(0);
  const excelInputRef = useRef(null);
  const [excelImporting, setExcelImporting] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'uploadDate',
    direction: 'desc',
  });
  const [activeHeaderFilter, setActiveHeaderFilter] = useState(null);
  const [pendingHeaderFilters, setPendingHeaderFilters] = useState({});
  const [headerFilters, setHeaderFilters] = useState({});
  const headerFilterInputRef = useRef(null);
  const invoiceUploadInputRef = useRef(null);
  const [uploadingRowId, setUploadingRowId] = useState(null);
  const [pendingUploadRow, setPendingUploadRow] = useState(null);

  useEffect(() => {
    if (!activeHeaderFilter || !headerFilterInputRef.current) {
      return;
    }

    headerFilterInputRef.current.focus();
    headerFilterInputRef.current.select();
  }, [activeHeaderFilter]);

  useEffect(() => {
    const parsedUser = JSON.parse(localStorage.getItem('user'));
    const token = parsedUser?.token;
    if (!token) {
      setBranchesLoading(false);
      return;
    }

    setBranchesLoading(true);
    Services.GetPoints(token)
      .then((res) => {
        if (res.data.status && Array.isArray(res.data.result)) {
          console.log(res.data.result);
          setBranches(res.data.result);
        }
      })
      .catch(() => {
        setBranches([]);
      })
      .finally(() => {
        setBranchesLoading(false);
      });
  }, []);

  useEffect(() => {
    const fetchRows = async () => {
      if (startMonth.isAfter(endMonth, 'month')) {
        setRows([]);
        setDataWarning('Başlangıç ayı, bitiş ayından sonra olamaz.');
        return;
      }

      setLoading(true);
      setDataWarning('');
      try {
        const monthRange = buildMonthRange(startMonth, endMonth);
        const selectedBranch = branches.find(
          (branch) => String(branch.id) === String(selectedPoint)
        );
        const response = await Services.FinancialGetEsnafInvoiceList(
          selectedPoint || undefined,
          monthRange
        );
        const apiRows = buildRowsFromResponse(
          response?.data?.result,
          selectedPoint
            ? selectedBranch?.title || selectedBranch?.name
            : undefined
        );

        setRows(apiRows);
        setPage(1);
        if (apiRows.length === 0) {
          setDataWarning(
            'Seçilen ay aralığında listelenecek hakediş kaydı bulunamadı.'
          );
        }
      } catch (error) {
        console.error('[HakedisManagementPage] invoice-list error', error);
        setRows([]);
        setDataWarning('Hakediş verileri alınırken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchRows();
  }, [branches, endMonth, filterTick, selectedPoint, startMonth]);

  const filteredRows = useMemo(() => {
    let result = rows;

    if (statusFilter) {
      result = result.filter((row) => row.invoiceStatus === statusFilter);
    }

    if (showSelectedOnly) {
      result = result.filter((row) => selectedIds.includes(row.id));
    }

    const min = priceMin !== '' ? Number(priceMin) : null;
    const max = priceMax !== '' ? Number(priceMax) : null;

    if (min !== null) {
      result = result.filter((row) => row.amount >= min);
    }
    if (max !== null) {
      result = result.filter((row) => row.amount <= max);
    }

    Object.entries(headerFilters).forEach(([key, filterValue]) => {
      const normalizedFilter = normalizeHeaderSearchValue(filterValue);
      if (!normalizedFilter) {
        return;
      }

      result = result.filter((row) => {
        let comparableValue = row?.[key];

        if (key === 'month') {
          comparableValue = formatMonthLabel(row?.month);
        } else if (key === 'paymentDate' || key === 'uploadDate') {
          comparableValue = formatDate(row?.[key]);
        } else if (key === 'amount') {
          comparableValue = formatCurrency(row?.amount);
        }

        return normalizeHeaderSearchValue(comparableValue).includes(
          normalizedFilter
        );
      });
    });

    if (sortConfig.key) {
      result = [...result].sort((firstRow, secondRow) => {
        let firstValue = firstRow?.[sortConfig.key];
        let secondValue = secondRow?.[sortConfig.key];

        if (sortConfig.key === 'month') {
          firstValue = dayjs(`${firstRow?.month || ''}-01`).valueOf();
          secondValue = dayjs(`${secondRow?.month || ''}-01`).valueOf();
        } else if (
          sortConfig.key === 'paymentDate' ||
          sortConfig.key === 'uploadDate'
        ) {
          firstValue = normalizeDateValue(firstValue)?.valueOf() || 0;
          secondValue = normalizeDateValue(secondValue)?.valueOf() || 0;
        } else if (sortConfig.key === 'amount') {
          firstValue = Number(firstValue || 0);
          secondValue = Number(secondValue || 0);
        } else {
          firstValue = normalizeHeaderSearchValue(firstValue);
          secondValue = normalizeHeaderSearchValue(secondValue);
        }

        if (firstValue < secondValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (firstValue > secondValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [
    rows,
    statusFilter,
    showSelectedOnly,
    selectedIds,
    priceMin,
    priceMax,
    headerFilters,
    sortConfig,
  ]);

  const paginatedRows = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredRows.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const filteredBranches = useMemo(() => {
    const normalizedFilter = pointFilterText.trim().toLocaleLowerCase('tr');

    if (!normalizedFilter) {
      return branches;
    }

    return branches.filter((branch) => {
      const title = String(
        branch?.title || branch?.name || ''
      ).toLocaleLowerCase('tr');
      return title.includes(normalizedFilter);
    });
  }, [branches, pointFilterText]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  useEffect(() => {
    setPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    if (selectedIds.length === 0 && showSelectedOnly) {
      setShowSelectedOnly(false);
    }
  }, [selectedIds, showSelectedOnly]);

  const handleToggleSelection = (rowId) => {
    setSelectedIds((current) =>
      current.includes(rowId)
        ? current.filter((item) => item !== rowId)
        : [...current, rowId]
    );
  };

  const areAllFilteredRowsSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selectedIds.includes(row.id));

  const isSomeFilteredRowsSelected =
    filteredRows.some((row) => selectedIds.includes(row.id)) &&
    !areAllFilteredRowsSelected;

  const handleToggleSelectAll = () => {
    if (filteredRows.length === 0) {
      return;
    }

    if (areAllFilteredRowsSelected) {
      const filteredRowIds = new Set(filteredRows.map((row) => row.id));
      setSelectedIds((current) =>
        current.filter((id) => !filteredRowIds.has(id))
      );
      return;
    }

    setSelectedIds((current) => {
      const next = new Set(current);
      filteredRows.forEach((row) => next.add(row.id));
      return Array.from(next);
    });
  };

  const handleBulkUpdate = async () => {
    const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
    const invoiceIDs = selectedRows.map((row) => row.invoiceID).filter(Boolean);
    const statusCode = STATUS_VALUE_MAP[bulkStatus];

    if (!statusCode) {
      toast.error('Geçerli bir statü seçilemedi.');
      return;
    }

    if (invoiceIDs.length === 0) {
      toast.error('Güncellenecek fatura numarası bulunamadı.');
      return;
    }

    setBulkUpdating(true);
    try {
      await Services.FinancialMassInvoiceStatusUpdate(invoiceIDs, statusCode);
      setRows((current) =>
        current.map((row) =>
          selectedIds.includes(row.id)
            ? { ...row, invoiceStatus: bulkStatus }
            : row
        )
      );
      setSelectedIds([]);
      setModalOpen(false);
      toast.success('Seçili hakediş kayıtlarının statüsü güncellendi.');
    } catch (error) {
      console.error('[HakedisManagementPage] mass status update error', error);
      toast.error('Toplu statü güncelleme sırasında bir hata oluştu.');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleExportSelectedRows = () => {
    const selectedRows = rows.filter((row) => selectedIds.includes(row.id));

    if (selectedRows.length === 0) {
      toast.error('Excel aktarımı için seçili kayıt bulunamadı.');
      return;
    }

    try {
      const exportRows = selectedRows.map((row) => ({
        'Nokta Adı': row.pointName || '-',
        'Hakediş Ayı': formatMonthLabel(row.month),
        'Ödeme Tarihi': formatDate(row.paymentDate),
        'Yüklenme Tarihi': formatDate(row.uploadDate),
        'Yükleme Durumu': row.uploadStatus || '-',
        'Görülme Durumu': row.isRead || '-',
        'Fatura Statüsü': row.invoiceStatus || '-',
        Tutar: Number(row.amount || 0),
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      worksheet['!cols'] = [
        { wch: 28 },
        { wch: 18 },
        { wch: 16 },
        { wch: 18 },
        { wch: 16 },
        { wch: 16 },
        { wch: 20 },
        { wch: 14 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Hakedişler');

      const fileName = `Hakedis_Yonetimi_${dayjs().format(
        'YYYYMMDD_HHmm'
      )}.xlsx`;

      XLSX.writeFile(workbook, fileName);
      toast.success(`${selectedRows.length} kayıt Excel'e aktarıldı.`);
    } catch (error) {
      console.error('[HakedisManagementPage] excel export error', error);
      toast.error('Excel aktarımı sırasında bir hata oluştu.');
    }
  };

  const handleNavigateDetail = (row) => {
    sessionStorage.setItem(`hakedis-detail-${row.id}`, JSON.stringify(row));
    navigate(`/dashboard/hakedis-management/${row.id}`, {
      state: { row },
    });
  };

  const handleExcelButtonClick = () => {
    if (excelImporting || branchesLoading) return;
    excelInputRef.current?.click();
  };

  const handleExcelFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setExcelImporting(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const excelRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const subeKoduKeys = new Set();
      const noktaIdKeys = new Set();

      excelRows.forEach((row) => {
        const subeKodu = row[EXCEL_SUBE_KODU_KEY];
        const noktaId = row[EXCEL_NOKTA_ID_KEY];
        if (subeKodu !== undefined && subeKodu !== '')
          subeKoduKeys.add(normalizeMatchKey(subeKodu));
        if (noktaId !== undefined && noktaId !== '')
          noktaIdKeys.add(normalizeMatchKey(noktaId));
      });

      if (subeKoduKeys.size === 0 && noktaIdKeys.size === 0) {
        toast.error(
          `Excel dosyasında "${EXCEL_SUBE_KODU_KEY}" veya "${EXCEL_NOKTA_ID_KEY}" sütunu bulunamadı.`
        );
        return;
      }

      const matchedBranchIds = new Set(
        branches
          .filter((branch) => {
            const branchCodeKey = normalizeMatchKey(branch.branchCode);
            const branchIdKey = normalizeMatchKey(branch.id);
            return (
              (branchCodeKey && subeKoduKeys.has(branchCodeKey)) ||
              (branchIdKey && noktaIdKeys.has(branchIdKey))
            );
          })
          .map((branch) => String(branch.id))
      );

      const matchedRowIds = rows
        .filter((row) => matchedBranchIds.has(String(row.pointId)))
        .map((row) => row.id);

      if (matchedRowIds.length === 0) {
        toast.error('Excel dosyasındaki kayıtlarla eşleşen satır bulunamadı.');
        return;
      }

      setSelectedIds(matchedRowIds);
      setShowSelectedOnly(true);
      setPage(1);
      toast.success(`${matchedRowIds.length} kayıt eşleşti ve seçildi.`);
    } catch (error) {
      console.error('[HakedisManagementPage] excel import error', error);
      toast.error('Excel dosyası okunurken bir hata oluştu.');
    } finally {
      setExcelImporting(false);
      event.target.value = '';
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const base64Content = result.includes(',')
          ? result.split(',').pop()
          : result;
        resolve(base64Content);
      };
      reader.onerror = () => reject(reader.error || new Error('Dosya okunamadi.'));
      reader.readAsDataURL(file);
    });

  const handleInvoiceUploadButtonClick = (row) => {
    if (!row?.invoiceID || !row?.pointId || !row?.month || uploadingRowId) {
      return;
    }

    setPendingUploadRow(row);
    invoiceUploadInputRef.current?.click();
  };

  const handleInvoiceFileChange = async (event) => {
    const file = event.target.files?.[0];
    const targetRow = pendingUploadRow;

    if (!file || !targetRow) {
      event.target.value = '';
      return;
    }

    try {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension) {
        throw new Error('Dosya uzantısı bulunamadı.');
      }

      setUploadingRowId(targetRow.id);
      const base64File = await toBase64(file);
      const response = await Services.FinancialUploadEsnafInvoice(
        targetRow.pointId,
        targetRow.month,
        base64File,
        extension,
        targetRow.invoiceID
      );

      if (!response?.data?.status) {
        throw new Error(response?.data?.message || 'Fatura yüklenemedi.');
      }

      setRows((current) =>
        current.map((row) =>
          row.id === targetRow.id
            ? {
                ...row,
                invoiceStatus: 'Fatura Yüklendi',
                uploadStatus: 'Yüklendi',
                uploadDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                invoiceFileURL:
                  response?.data?.result?.invoiceFileURL || row.invoiceFileURL,
                rawItem: {
                  ...row.rawItem,
                  invoiceFileURL:
                    response?.data?.result?.invoiceFileURL || row.invoiceFileURL,
                  uploadDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                  status: '4',
                },
              }
            : row
        )
      );
      toast.success('Fatura başarıyla yüklendi.');
    } catch (error) {
      console.error('[HakedisManagementPage] invoice upload error', error);
      toast.error(error?.message || 'Fatura yüklenirken bir hata oluştu.');
    } finally {
      setUploadingRowId(null);
      setPendingUploadRow(null);
      event.target.value = '';
    }
  };

  const handleHeaderSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        key,
        direction: 'asc',
      };
    });
  };

  const handleHeaderFilterOpen = (key) => {
    setActiveHeaderFilter(key);
    setPendingHeaderFilters((current) => ({
      ...current,
      [key]: current[key] ?? headerFilters[key] ?? '',
    }));
  };

  const handleHeaderFilterApply = (key) => {
    setHeaderFilters((current) => ({
      ...current,
      [key]: pendingHeaderFilters[key] ?? '',
    }));
    setActiveHeaderFilter(null);
    setPage(1);
  };

  const renderHeaderCell = (key, label) => {
    const isActiveFilter = activeHeaderFilter === key;
    const hasFilter = Boolean(headerFilters[key]?.trim());
    const isSorted = sortConfig.key === key;

    return (
      <TableCell
        onClick={() => handleHeaderSort(key)}
        onDoubleClick={() => handleHeaderFilterOpen(key)}
        sx={{
          borderBottom: '1px solid #F6E0E0',
          color: '#6B7280',
          fontSize: 12,
          cursor: 'pointer',
          userSelect: 'none',
          minWidth: key === 'pointName' ? 170 : 'auto',
        }}
      >
        {isActiveFilter ? (
          <TextField
            inputRef={headerFilterInputRef}
            size="small"
            fullWidth
            value={pendingHeaderFilters[key] ?? ''}
            placeholder="Ara"
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
            onBlur={() => setActiveHeaderFilter(null)}
            onChange={(event) =>
              setPendingHeaderFilters((current) => ({
                ...current,
                [key]: event.target.value,
              }))
            }
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') {
                handleHeaderFilterApply(key);
              }
              if (event.key === 'Escape') {
                setActiveHeaderFilter(null);
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 28,
                fontSize: 12,
                backgroundColor: '#FFFFFF',
              },
              '& .MuiOutlinedInput-input': {
                px: 1.25,
                py: 0.75,
              },
            }}
          />
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: hasFilter || isSorted ? 700 : 600,
                color: hasFilter ? '#E24337' : '#6B7280',
              }}
            >
              {label}
            </Typography>
            {isSorted ? (
              sortConfig.direction === 'asc' ? (
                <ArrowUpwardRoundedIcon sx={{ fontSize: 14, color: '#E24337' }} />
              ) : (
                <ArrowDownwardRoundedIcon sx={{ fontSize: 14, color: '#E24337' }} />
              )
            ) : null}
          </Stack>
        )}
      </TableCell>
    );
  };

  return (
    <>
      <Helmet>
        <title>Hakediş Yönetimi</title>
      </Helmet>

      <Box sx={{ px: { xs: 2, lg: 5 }, pb: 5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2.5, mt: 1 }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#2A2E3F' }}>
            Hakediş Yönetimi
          </Typography>

          <Stack direction="row" spacing={1.25} sx={{ ml: 'auto' }}>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={excelInputRef}
              style={{ display: 'none' }}
              onChange={handleExcelFileChange}
            />
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              ref={invoiceUploadInputRef}
              style={{ display: 'none' }}
              onChange={handleInvoiceFileChange}
            />
            <Button
              variant="outlined"
              disableElevation
              disabled={excelImporting || branchesLoading}
              onClick={handleExcelButtonClick}
              startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{
                px: 2.25,
                height: 40,
                borderRadius: '8px',
                color: '#4B5563',
                borderColor: '#E5E7EB',
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                  borderColor: '#E5E7EB',
                },
              }}
            >
              {excelImporting
                ? 'Yükleniyor...'
                : branchesLoading
                ? 'Noktalar Yükleniyor...'
                : "Excel'den Aktar"}
            </Button>

            <Button
              variant="contained"
              disableElevation
              disabled={selectedIds.length === 0}
              onClick={handleExportSelectedRows}
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{
                px: 2.25,
                height: 40,
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#F9FAFB', boxShadow: 'none' },
                '&.Mui-disabled': {
                  backgroundColor: '#F3F4F6',
                  color: '#A0A7B4',
                },
              }}
            >
              Excel'e Aktar
            </Button>

            <Button
              variant="contained"
              disableElevation
              disabled={selectedIds.length === 0}
              onClick={() => setModalOpen(true)}
              sx={{
                px: 2.25,
                height: 40,
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#F9FAFB', boxShadow: 'none' },
                '&.Mui-disabled': {
                  backgroundColor: '#F3F4F6',
                  color: '#A0A7B4',
                },
              }}
            >
              Toplu Güncelle
            </Button>
          </Stack>
        </Stack>

        {dataWarning ? (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
            {dataWarning}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: '14px',
            border: '1px solid #EEF0F4',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(18, 38, 63, 0.05)',
          }}
        >
          <Box sx={{ p: 2.25, borderBottom: '1px solid #F1F3F7' }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
                <FormControl fullWidth>
                  <Select
                    displayEmpty
                    value={selectedPoint}
                    onChange={(event) => setSelectedPoint(event.target.value)}
                    onClose={() => setPointFilterText('')}
                    IconComponent={ExpandMoreRoundedIcon}
                    renderValue={(value) => {
                      if (!value) return 'Tüm Noktalar';
                      return (
                        branches.find((branch) => branch.id === value)?.title ||
                        'Tüm Noktalar'
                      );
                    }}
                    MenuProps={{
                      autoFocus: false,
                      PaperProps: {
                        sx: {
                          maxHeight: 360,
                          minWidth: selectMenuWidth,
                          mt: 0.5,
                        },
                      },
                      MenuListProps: {
                        sx: {
                          pt: 0,
                        },
                      },
                    }}
                    sx={{
                      height: 44,
                      borderRadius: '8px',
                      color: '#4B5563',
                      backgroundColor: '#FFFFFF',
                      minWidth: selectMenuWidth,
                    }}
                  >
                    <ListSubheader
                      sx={{ backgroundColor: '#FFFFFF', py: 1.25 }}
                    >
                      <TextField
                        autoFocus
                        size="small"
                        fullWidth
                        placeholder="Nokta adına göre ara"
                        value={pointFilterText}
                        onChange={(event) =>
                          setPointFilterText(event.target.value)
                        }
                        onKeyDown={(event) => event.stopPropagation()}
                        inputProps={{ 'aria-label': 'Nokta filtrele' }}
                      />
                    </ListSubheader>
                    <MenuItem value="">Tüm Noktalar</MenuItem>
                    {filteredBranches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.title || branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <Select
                    value={startMonth.format('YYYY-MM')}
                    onChange={(event) =>
                      setStartMonth(
                        dayjs(`${event.target.value}-01`).startOf('month')
                      )
                    }
                    IconComponent={ExpandMoreRoundedIcon}
                    sx={{
                      height: 44,
                      borderRadius: '8px',
                      color: '#4B5563',
                      backgroundColor: '#FFFFFF',
                      minWidth: selectMenuWidth,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          minWidth: selectMenuWidth,
                        },
                      },
                    }}
                  >
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <Select
                    value={endMonth.format('YYYY-MM')}
                    onChange={(event) =>
                      setEndMonth(
                        dayjs(`${event.target.value}-01`).startOf('month')
                      )
                    }
                    IconComponent={ExpandMoreRoundedIcon}
                    sx={{
                      height: 44,
                      borderRadius: '8px',
                      color: '#4B5563',
                      backgroundColor: '#FFFFFF',
                      minWidth: selectMenuWidth,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          minWidth: selectMenuWidth,
                        },
                      },
                    }}
                  >
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <Select
                    displayEmpty
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    IconComponent={ExpandMoreRoundedIcon}
                    renderValue={(value) => value || 'Statü Durumu'}
                    sx={{
                      height: 44,
                      borderRadius: '8px',
                      minWidth: selectMenuWidth,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          minWidth: selectMenuWidth,
                        },
                      },
                    }}
                  >
                    <MenuItem value="">Tüm Statüler</MenuItem>
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedPoint('');
                    setStatusFilter('');
                    setStartMonth(MIN_MONTH);
                    setEndMonth(dayjs().startOf('month'));
                    setRows([]);
                    setSelectedIds([]);
                    setPage(1);
                    setDataWarning('');
                    setShowSelectedOnly(false);
                    setPriceMin('');
                    setPriceMax('');
                    setSortConfig({ key: 'uploadDate', direction: 'desc' });
                    setHeaderFilters({});
                    setPendingHeaderFilters({});
                    setActiveHeaderFilter(null);
                  }}
                  sx={{
                    minWidth: 92,
                    height: 44,
                    borderRadius: '8px',
                    borderColor: '#E5E7EB',
                    color: '#4B5563',
                  }}
                >
                  Temizle
                </Button>

                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => setFilterTick((current) => current + 1)}
                  sx={{
                    minWidth: 84,
                    height: 44,
                    borderRadius: '8px',
                    backgroundColor: '#E24337',
                    '&:hover': { backgroundColor: '#D53A2F' },
                  }}
                >
                  Filtrele
                </Button>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="start"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showSelectedOnly}
                      onChange={(event) =>
                        setShowSelectedOnly(event.target.checked)
                      }
                      disabled={selectedIds.length === 0}
                      size="small"
                      sx={{
                        color: '#C3CAD6',
                        '&.Mui-checked': { color: '#E24337' },
                      }}
                    />
                  }
                  label="Seçilenleri Göster"
                  sx={{
                    color: selectedIds.length === 0 ? '#C3CAD6' : '#4B5563',
                    whiteSpace: 'nowrap',
                    m: 0,
                  }}
                />

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    borderColor: '#E5E7EB',
                    mx: '14px !important',
                  }}
                />

                <Stack direction="row" alignItems="center" spacing={1}>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Min ₺"
                    value={priceMin}
                    onChange={(event) => setPriceMin(event.target.value)}
                    sx={{ width: 110 }}
                  />
                  <Typography sx={{ color: '#98A2B3', fontSize: 13 }}>
                    -
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Max ₺"
                    value={priceMax}
                    onChange={(event) => setPriceMax(event.target.value)}
                    sx={{ width: 110 }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#FFF1F1' }}>
                  <TableCell
                    padding="checkbox"
                    sx={{ width: 44, borderBottom: '1px solid #F6E0E0' }}
                  >
                    <Checkbox
                      checked={areAllFilteredRowsSelected}
                      indeterminate={isSomeFilteredRowsSelected}
                      onChange={handleToggleSelectAll}
                      size="small"
                      sx={{
                        color: '#C3CAD6',
                        '&.Mui-checked': { color: '#E24337' },
                        '&.MuiCheckbox-indeterminate': { color: '#E24337' },
                      }}
                    />
                  </TableCell>
                  {HEADER_FILTER_FIELDS.map((field) =>
                    renderHeaderCell(field.key, field.label)
                  )}
                  <TableCell
                    sx={{
                      width: 56,
                      borderBottom: '1px solid #F6E0E0',
                      color: '#6B7280',
                      fontSize: 12,
                    }}
                  >
                    Yükle
                  </TableCell>
                  <TableCell
                    sx={{ width: 92, borderBottom: '1px solid #F6E0E0' }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ '& td': { borderBottom: '1px solid #F2F4F7' } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(row.id)}
                        onChange={() => handleToggleSelection(row.id)}
                        size="small"
                        sx={{
                          color: '#C3CAD6',
                          '&.Mui-checked': { color: '#E24337' },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#4B5563', fontSize: 14 }}>
                      {row.pointName}
                    </TableCell>
                    <TableCell sx={{ color: '#6B7280', fontSize: 13 }}>
                      {formatMonthLabel(row.month)}
                    </TableCell>
                    <TableCell sx={{ color: '#6B7280', fontSize: 13 }}>
                      {formatDate(row.paymentDate)}
                    </TableCell>
                    <TableCell sx={{ color: '#6B7280', fontSize: 13 }}>
                      {formatDate(row.uploadDate)}
                    </TableCell>
                    <TableCell sx={{ color: '#4B5563', fontSize: 13 }}>
                      {row.uploadStatus}
                    </TableCell>
                    <TableCell sx={{ color: '#4B5563', fontSize: 13 }}>
                      {row.isRead}
                    </TableCell>
                    <TableCell>
                      <StatusChip label={row.invoiceStatus} />
                    </TableCell>
                    <TableCell
                      sx={{ color: '#374151', fontWeight: 500, fontSize: 13 }}
                    >
                      {formatCurrency(row.amount)}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        onClick={() => handleInvoiceUploadButtonClick(row)}
                        disabled={uploadingRowId === row.id}
                        sx={{
                          minWidth: 30,
                          width: 30,
                          p: 0.5,
                          color: '#8F96A3',
                        }}
                      >
                        {uploadingRowId === row.id ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={0.5}
                      >
                        <Box
                          sx={{
                            minWidth: 30,
                            width: 30,
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          {row.invoiceFileURL ? (
                            <Button
                              component="a"
                              href={row.invoiceFileURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                minWidth: 30,
                                width: 30,
                                p: 0.5,
                                color: '#8F96A3',
                              }}
                            >
                              <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                            </Button>
                          ) : null}
                        </Box>
                        <Button
                          onClick={() => handleNavigateDetail(row)}
                          sx={{
                            minWidth: 30,
                            width: 30,
                            p: 0.5,
                            color: '#8F96A3',
                          }}
                        >
                          <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      align="center"
                      sx={{ py: 5, color: '#6B7280' }}
                    >
                      Listelenecek kayıt bulunamadı.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2.25, py: 1.75, borderTop: '1px solid #F1F3F7' }}
          >
            <Typography sx={{ color: '#98A2B3', fontSize: 13 }}>
              {filteredRows.length > 0
                ? `${filteredRows.length} kayıt içinden ${
                    (page - 1) * rowsPerPage + 1
                  }-${Math.min(
                    page * rowsPerPage,
                    filteredRows.length
                  )} arası gösteriliyor`
                : '0 kayıt gösteriliyor'}
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={3}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  sx={{ color: '#6B7280', fontSize: 13, whiteSpace: 'nowrap' }}
                >
                  Gösterilen Satır Sayısı
                </Typography>
                <Select
                  variant="standard"
                  disableUnderline
                  value={rowsPerPage}
                  onChange={(event) =>
                    setRowsPerPage(Number(event.target.value))
                  }
                  IconComponent={ExpandMoreRoundedIcon}
                  sx={{
                    fontSize: 13,
                    color: '#4B5563',
                    '& .MuiSelect-select': {
                      py: 0,
                      pr: '20px !important',
                      backgroundColor: 'transparent',
                    },
                    '& .MuiSelect-select:focus': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>

              <Typography
                sx={{ color: '#6B7280', fontSize: 13, whiteSpace: 'nowrap' }}
              >
                {filteredRows.length > 0
                  ? `${(page - 1) * rowsPerPage + 1}-${Math.min(
                      page * rowsPerPage,
                      filteredRows.length
                    )} of ${filteredRows.length}`
                  : '0 of 0'}
              </Typography>

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Button
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={page === 1}
                  sx={{
                    minWidth: 32,
                    width: 32,
                    height: 32,
                    p: 0,
                    borderRadius: '8px',
                    color: '#98A2B3',
                  }}
                >
                  <KeyboardArrowLeftRoundedIcon />
                </Button>
                <Button
                  onClick={() =>
                    setPage((current) => Math.min(pageCount, current + 1))
                  }
                  disabled={page === pageCount}
                  sx={{
                    minWidth: 32,
                    width: 32,
                    height: 32,
                    p: 0,
                    borderRadius: '8px',
                    color: '#98A2B3',
                  }}
                >
                  <KeyboardArrowRightRoundedIcon />
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        <Dialog
          open={modalOpen}
          onClose={() => (bulkUpdating ? null : setModalOpen(false))}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Toplu Statü Güncelle</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 2 }}>
              Seçili {selectedIds.length} kayıt için yeni statü belirleyin.
            </Typography>
            <FormControl fullWidth>
              <Select
                value={bulkStatus}
                onChange={(event) => setBulkStatus(event.target.value)}
                IconComponent={ExpandMoreRoundedIcon}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setModalOpen(false)}
              sx={{ color: '#6B7280' }}
              disabled={bulkUpdating}
            >
              Vazgeç
            </Button>
            <Button
              variant="contained"
              onClick={handleBulkUpdate}
              disabled={bulkUpdating}
              sx={{ backgroundColor: '#E24337' }}
            >
              {bulkUpdating ? 'Güncelleniyor...' : 'Güncelle'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
